import { AbstractComponent } from "appworks/components/abstract-component";
import { Components } from "appworks/components/components";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { GraphicsService } from "appworks/graphics/graphics-service";
import { Layers } from "appworks/graphics/layers/layers";
import { TrailEmitter } from "appworks/graphics/particles/trail-emitter";
import { DualPosition } from "appworks/graphics/pixi/dual-position";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { TweenContract } from "appworks/utils/contracts/tween-contract";
import { Easing, Interpolation, Tween } from "appworks/utils/tween";
import * as particles from "pixi-particles";
import { Signal } from "signals";
import { SymbolSubcomponent } from "slotworks/components/matrix/symbol/symbol-subcomponent";
import { TrailDisplayComponent } from "./trail-display";

export interface AgsSparkConfig {
    tweenDuration: number;
    emitterShutdownDelay: number;
    tweenEasing: (k: number) => number;
    tweenInterpolation: (v: number[], k: number) => number;
}

export class SparkComponent extends AbstractComponent {
    public onParticlesGone: Signal;

    protected config: AgsSparkConfig = {
        tweenDuration: 1300,
        emitterShutdownDelay: 200,
        tweenEasing: Easing.Quadratic.In,
        tweenInterpolation: Interpolation.Bezier
    }

    protected emitter: particles.Emitter;
    protected emitterId: number;
    protected trailLayer: Layers = null;
    protected symbol: SymbolSubcomponent;
    protected destination: DualPosition;

    protected sparkSprite: Sprite;
    protected sparkTrail: TrailEmitter;

    public constructor(symbol: SymbolSubcomponent, destination: DualPosition, layer: Layers) {
        super();

        this.symbol = symbol;
        this.destination = destination;

        this.trailLayer = layer;

        this.createSparkSprite();
        this.createTrailEmitter();
    }

    public fire(): Contract<void> {
        Services.get(SoundService).customEvent("spark_fire");

        const path = this.getSparkPath();

        return new Parallel([
            () => this.animateSpark(path),
            () => this.sparkTrail.play(path)
        ]);
    }

    public destroy() {
        //
    }

    public animateSpark(path: DualPosition[]): Contract<void> {
        if (!this.sparkSprite) { return Contract.empty(); }

        this.sparkSprite.dualPosition = path[0];

        const tweens = [];

        [Orientation.LANDSCAPE, Orientation.PORTRAIT].forEach((orientation) => {
            const xPositions = path.map((point) => point[orientation].x);
            const yPositions = path.map((point) => point[orientation].y);

            tweens.push(new Tween(this.sparkSprite[orientation])
                .to({ x: xPositions, y: yPositions }, this.config.tweenDuration)
                .easing(this.config.tweenEasing)
                .interpolation(this.config.tweenInterpolation)
            );
        });

        return new Parallel(tweens.map((tween) => { return () => TweenContract.wrapTween(tween) }));
    }

    protected createSparkSprite() {
        this.sparkSprite = Services.get(GraphicsService).createSprite("spark");
        this.sparkSprite.anchor.set(0.5, 0.5);
        this.trailLayer.add(this.sparkSprite);
    }

    protected createTrailEmitter(emitterNames: string[] = ["sparkTrail"]) {
        const tweenConfig = {
            duration: this.config.tweenDuration,
            emitterShutdownDelay: this.config.emitterShutdownDelay
        };

        this.sparkTrail = new TrailEmitter(this.trailLayer, emitterNames, tweenConfig);
        this.onParticlesGone = this.sparkTrail.onParticlesGone;
    }

    protected getSparkPath(): DualPosition[] {
        const symbolPosition = Layers.get("MatrixContent").localToLocal(
            this.trailLayer,
            Layers.get("MatrixContent").getPosition(`symbol_${this.symbol.gridPosition.x}_${this.symbol.gridPosition.y}`)
        );

        const endPosition = Components.get(TrailDisplayComponent).getPosition();

        return [symbolPosition, endPosition];
    }
}
